/**
 * Simple slideshow functionality
 * @returns {Object} Slideshow methods
 */
const Slideshow = () => {
    let slideshows = [];
    let intervals = {};
    let currentSlides = {};
    let isPaused = false;

    /**
     * Show a specific slide in a slideshow
     * @param {HTMLElement} slideshow - The slideshow container
     * @param {number} slideIndex - The index of the slide to show
     */
    const showSlide = (slideshow, slideIndex) => {
        const slides = slideshow.querySelectorAll('.slide');
        const id = slideshow.getAttribute('data-slideshow');

        // Update current slide index
        currentSlides[id] = slideIndex;

        // Hide all slides
        slides.forEach(slide => {
            slide.classList.add('hidden');
        });


        // If no dots, just show the selected slide
        slides[slideIndex].classList.remove('hidden');
    };

    /**
     * Navigate to the previous slide
     * @param {HTMLElement} slideshow - The slideshow container
     */
    const prevSlide = (slideshow) => {
        const id = slideshow.getAttribute('data-slideshow');
        const slides = slideshow.querySelectorAll('.slide');
        let currentSlide = currentSlides[id] || 0;

        // Calculate the previous slide index (with wrap-around)
        currentSlide = (currentSlide - 1 + slides.length) % slides.length;

        // Show the previous slide
        showSlide(slideshow, currentSlide);

        // Reset the auto-slide timer
        if (!isPaused) {
            startAutoSlide(slideshow);
        }
    };

    /**
     * Navigate to the next slide
     * @param {HTMLElement} slideshow - The slideshow container
     */
    const nextSlide = (slideshow) => {
        const id = slideshow.getAttribute('data-slideshow');
        const slides = slideshow.querySelectorAll('.slide');
        let currentSlide = currentSlides[id] || 0;

        // Calculate the next slide index (with wrap-around)
        currentSlide = (currentSlide + 1) % slides.length;

        // Show the next slide
        showSlide(slideshow, currentSlide);

        // Reset the auto-slide timer
        if (!isPaused) {
            startAutoSlide(slideshow);
        }
    };

    /**
     * Start automatic slideshow
     * @param {HTMLElement} slideshow - The slideshow container
     * @param {number} interval - Time between slides in milliseconds
     */
    const startAutoSlide = (slideshow, interval = 5000) => {
        if (isPaused) return;

        const id = slideshow.getAttribute('data-slideshow');

        // Initialize current slide if not set
        if (currentSlides[id] === undefined) {
            currentSlides[id] = 0;
        }

        // Clear any existing interval
        if (intervals[id]) {
            clearInterval(intervals[id]);
        }

        // Set new interval
        intervals[id] = setInterval(() => {
            nextSlide(slideshow);
        }, interval);
    };

    /**
     * Pause all slideshows
     */
    const pauseAllSlideshows = () => {
        isPaused = true;
        Object.keys(intervals).forEach(id => {
            if (intervals[id]) {
                clearInterval(intervals[id]);
                intervals[id] = null;
            }
        });
    };

    /**
     * Resume all slideshows
     */
    const resumeAllSlideshows = () => {
        isPaused = false;
        slideshows.forEach(slideshow => {
            startAutoSlide(slideshow);
        });
    };

    /**
     * Initialize slideshows
     */
    const init = () => {
        slideshows = document.querySelectorAll('.slideshow');

        slideshows.forEach(slideshow => {
            const prevButton = slideshow.querySelector('.slide-nav.prev');
            const nextButton = slideshow.querySelector('.slide-nav.next');
            const id = slideshow.getAttribute('data-slideshow');

            // Initialize current slide
            currentSlides[id] = 0;
            // Add click event to prev button
            if (prevButton) {
                prevButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    prevSlide(slideshow);
                });
            }

            // Add click event to next button
            if (nextButton) {
                nextButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    nextSlide(slideshow);
                });
            }

            // Start auto-sliding
            startAutoSlide(slideshow);
        });
    };

    return {
        init,
        pauseAllSlideshows,
        resumeAllSlideshows
    };
};

export default Slideshow;
